import { useCallback, useRef, useState } from "react";
import {
  pipelineService,
  type FranceToParisMappedFilters,
} from "../../../../api/pipeline.service.js";

export type FranceToParisMappedProgress = {
  copied: number;
  remaining: number | null;
  batch: number;
};

const EMPTY_PROGRESS: FranceToParisMappedProgress = {
  copied: 0,
  remaining: null,
  batch: 0,
};

// Drives the UI batch loop: repeatedly calls franceToParisMappedBatch with the
// chosen filters + mappings, accumulating inserted rows, until remaining hits 0,
// a stop is requested, or the no-progress guard trips.
export function useFranceToParisMappedCopy(onComplete?: () => void) {
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<FranceToParisMappedProgress>(EMPTY_PROGRESS);
  const [error, setError] = useState<string | null>(null);
  const stopRef = useRef(false);

  const stop = useCallback(() => {
    stopRef.current = true;
  }, []);

  const start = useCallback(
    async (filters: FranceToParisMappedFilters, batchSize: number) => {
      stopRef.current = false;
      setRunning(true);
      setError(null);
      setProgress(EMPTY_PROGRESS);

      let copied = 0;
      let batch = 0;

      try {
        while (!stopRef.current) {
          batch += 1;
          const result = await pipelineService.franceToParisMappedBatch({
            ...filters,
            batchSize,
          });
          copied += result.inserted;
          setProgress({ copied, remaining: result.remaining, batch });

          if (result.remaining <= 0) break;

          // Runaway guard: work remains but nothing was inserted this batch.
          if (result.inserted === 0) {
            setError(`Stopped: batch ${batch} inserted nothing while ${result.remaining} remain.`);
            break;
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "France to Paris (mapped) batch failed");
      } finally {
        setRunning(false);
        onComplete?.();
      }
    },
    [onComplete],
  );

  return { running, progress, error, start, stop };
}
